import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Users,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Shield,
  ShieldCheck,
  ShieldOff,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  Activity,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ArrowUpRight,
  Mail,
  Settings,
  Ban,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreHorizontal,
  KeyRound
} from 'lucide-react'
import { motion, AnimatePresence, MotionConfig } from 'framer-motion'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import Modal from '../common/Modal'
import { toast } from 'react-hot-toast'

const inputStyle = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
}

const selectStyle = {
  ...inputStyle,
  appearance: 'none',
  cursor: 'pointer',
}

const UserManager = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRole, setSelectedRole] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [userToDelete, setUserToDelete] = useState(null)
  const [resetPasswordUser, setResetPasswordUser] = useState(null)
  const queryClient = useQueryClient()
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  // Only animate once on initial load
  useEffect(() => {
    if (!hasInitiallyLoaded) {
      const timer = setTimeout(() => {
        setHasInitiallyLoaded(true)
      }, 1000) // Wait for animations to complete
      return () => clearTimeout(timer)
    }
  }, [])

  const { data: usersData, isLoading, error } = useQuery(
    ['users'],
    () => apiService.get('/users'),
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: 1
    }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  )

  // Transform API data to match expected format
  const users = usersData?.data || usersData

  // Mock data for demo
  const mockUsers = [
    {
      id: 1,
      name: 'John Doe',
      email: 'admin@webnest.com',
      role: 'admin',
      status: 'active',
      created_at: '2024-01-01T00:00:00Z',
      last_login: '2024-01-15T10:30:00Z',
      login_count: 145,
      permissions: ['all'],
      is_verified: true
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'demo@client.com',
      role: 'client',
      status: 'active',
      created_at: '2024-01-10T00:00:00Z',
      last_login: '2024-01-14T15:20:00Z',
      login_count: 32,
      permissions: ['read', 'write'],
      is_verified: true
    },
    {
      id: 3,
      name: 'Bob Wilson',
      email: 'bob@example.com',
      role: 'client',
      status: 'inactive',
      created_at: '2023-12-15T00:00:00Z',
      last_login: '2024-01-12T14:15:00Z',
      login_count: 8,
      permissions: ['read'],
      is_verified: false
    },
    {
      id: 4,
      name: 'Alice Johnson',
      email: 'alice@company.com',
      role: 'admin',
      status: 'active',
      created_at: '2024-01-05T00:00:00Z',
      last_login: '2024-01-15T09:45:00Z',
      login_count: 89,
      permissions: ['all'],
      is_verified: true
    }
  ]

  const createUserMutation = useMutation(
    (userData) => apiService.post('/users', userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        setShowAddModal(false)
        toast.success('User created successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create user')
      }
    }
  )

  const updateUserMutation = useMutation(
    ({ id, ...userData }) => apiService.put(`/users/${id}`, userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        setEditingUser(null)
        toast.success('User updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user')
      }
    }
  )

  const resetPasswordMutation = useMutation(
    ({ id, password }) => apiService.patch(`/users/${id}/password`, { password }),
    {
      onSuccess: () => {
        setResetPasswordUser(null)
        toast.success('Password updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update password')
      }
    }
  )

  const deleteUserMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        toast.success('User deleted successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete user')
      }
    }
  )

  const reset2FAMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}/2fa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        setEditingUser(prev => prev ? { ...prev, totp_enabled: 0 } : prev)
        toast.success('2FA reset successfully')
      },
      onError: () => {
        toast.error('Failed to reset 2FA')
      }
    }
  )

  const forceMfaMutation = useMutation(
    (userId) => apiService.put(`/users/${userId}/force-mfa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
        setEditingUser(prev => prev ? { ...prev, mfa_required: 1 } : prev)
        toast.success('MFA setup will be required on next login')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to force MFA')
      }
    }
  )

  const cancelForceMfaMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}/force-mfa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
        setEditingUser(prev => prev ? { ...prev, mfa_required: 0 } : prev)
        toast.success('Forced MFA requirement removed')
      },
      onError: () => {
        toast.error('Failed to remove forced MFA')
      }
    }
  )

  // Transform API data to match expected format with proper status mapping
  const transformedUsers = users ? users.map(user => ({
    ...user,
    status: user.is_active ? 'active' : 'inactive',
    is_verified: true // Default to true for now, add email verification later if needed
  })) : []

  // Use real data if available, fallback to mock data
  const displayUsers = transformedUsers.length > 0 ? transformedUsers : (error ? mockUsers : [])

  // Client-side filtering with useMemo for performance
  const filteredUsers = useMemo(() => {
    return displayUsers.filter(user => {
      const matchesSearch = searchTerm === '' ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesRole = selectedRole === 'all' || user.role === selectedRole
      const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus
      return matchesSearch && matchesRole && matchesStatus
    })
  }, [displayUsers, searchTerm, selectedRole, selectedStatus])

  const handleDeleteUser = (user) => {
    setUserToDelete(user)
  }

  const confirmDelete = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.id)
      setUserToDelete(null)
    }
  }

  const handleToggleStatus = (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active'
    updateUserMutation.mutate({
      id: user.id,
      status: newStatus
    })
  }

  const RoleBadge = ({ role }) => {
    const roleColors = {
      admin:     { background: 'rgba(108,99,255,0.12)', color: 'var(--accent)' },
      client:    { background: 'rgba(91,192,235,0.15)', color: '#5BC0EB' },
      moderator: { background: 'rgba(146,141,171,0.15)', color: 'var(--text-muted)' },
    }
    const icons = {
      admin: Shield,
      client: UserCheck,
      moderator: Settings
    }
    const Icon = icons[role] || UserCheck
    const colors = roleColors[role] || roleColors.client

    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
        ...colors
      }}>
        <Icon style={{ width: 11, height: 11 }} />
        {role?.toUpperCase() || 'CLIENT'}
      </span>
    )
  }

  const StatusBadge = ({ status, isVerified }) => {
    const cfg = {
      active:   { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', Icon: CheckCircle },
      inactive: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', Icon: XCircle },
      banned:   { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', Icon: Ban },
    }
    const { bg, color, Icon } = cfg[status] || cfg.inactive
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: bg, color }}>
          <Icon style={{ width: 11, height: 11 }} />
          {status?.toUpperCase() || 'UNKNOWN'}
        </span>
        {!isVerified && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
            <AlertCircle style={{ width: 11, height: 11 }} />
            UNVERIFIED
          </span>
        )}
      </div>
    )
  }

  const StatCard = ({ title, value, icon: Icon, change, changeType = 'positive', index = 0 }) => {
    const card = (
      <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{title}</p>
            <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0, fontFamily: 'var(--font-mono)' }}>
              {value?.toLocaleString() || 0}
            </p>
          </div>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon style={{ width: 22, height: 22, color: '#fff' }} />
          </div>
        </div>
        {change !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {changeType === 'positive'
              ? <TrendingUp style={{ width: 14, height: 14, color: '#22c55e' }} />
              : <TrendingDown style={{ width: 14, height: 14, color: '#ef4444' }} />}
            <span style={{ fontSize: 12, fontWeight: 600, color: changeType === 'positive' ? '#22c55e' : '#ef4444' }}>
              {Math.abs(change)}%
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>vs last month</span>
          </div>
        )}
      </div>
    )

    if (hasInitiallyLoaded) return card
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
      >
        {card}
      </motion.div>
    )
  }

  const ResetPasswordForm = ({ user, onSubmit, onCancel, isLoading }) => {
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')

    const handleSubmit = (e) => {
      e.preventDefault()
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters')
        return
      }
      if (password !== confirm) {
        toast.error('Passwords do not match')
        return
      }
      onSubmit({ password })
    }

    return (
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
          Set a new password for <span style={{ fontWeight: 600, color: 'var(--text)' }}>{user.name}</span> ({user.email}).
        </p>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>New Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} placeholder="Enter new password" autoFocus />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>Confirm Password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={inputStyle} placeholder="Confirm new password" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Set Password'}
          </button>
        </div>
      </form>
    )
  }

  const UserForm = ({ user, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      name: user?.name || '',
      email: user?.email || '',
      role: user?.role || 'client',
      status: user?.status || 'active',
      subscription_plan: user?.subscription_plan || 'basic',
      password: '',
      confirmPassword: ''
    })

    const { data: plansRaw } = useQuery(
      'billing-plans',
      () => apiService.get('/billing/plans').then(r => r.data),
      { staleTime: 5 * 60 * 1000 }
    )

    const planOptions = plansRaw
      ? Object.entries(plansRaw).map(([id, plan]) => ({ id, name: plan.name || id, price: plan.price }))
      : [{ id: 'basic', name: 'Basic', price: null }, { id: 'pro', name: 'Pro', price: null }, { id: 'enterprise', name: 'Enterprise', price: null }]

    const handleSubmit = (e) => {
      e.preventDefault()
      if (!user && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }
      if (user && formData.password && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }
      const { confirmPassword, ...submitData } = formData
      if (user && !submitData.password) delete submitData.password
      onSubmit(submitData)
    }

    const labelStyle = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }

    return (
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={labelStyle}>Name</label>
          <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} style={inputStyle} required />
        </div>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} style={inputStyle} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Role</label>
            <div style={{ position: 'relative' }}>
              <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} style={selectStyle}>
                <option value="client">Client</option>
                <option value="admin">Admin</option>
                <option value="moderator">Moderator</option>
              </select>
              <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--text-faint)', pointerEvents: 'none' }} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <div style={{ position: 'relative' }}>
              <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} style={selectStyle}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="banned">Banned</option>
              </select>
              <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--text-faint)', pointerEvents: 'none' }} />
            </div>
          </div>
        </div>
        {formData.role === 'client' && (
          <div>
            <label style={labelStyle}>Subscription Plan</label>
            <div style={{ position: 'relative' }}>
              <select value={formData.subscription_plan} onChange={(e) => setFormData({...formData, subscription_plan: e.target.value})} style={selectStyle}>
                {planOptions.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.price != null ? ` — £${p.price}/mo` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--text-faint)', pointerEvents: 'none' }} />
            </div>
          </div>
        )}
        {!user && (
          <>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} style={inputStyle} required={!user} />
            </div>
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} style={inputStyle} required={!user} />
            </div>
          </>
        )}
        {user && (
          <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', margin: 0 }}>Two-Factor Authentication</p>
                <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                  {user.totp_enabled ? '2FA is currently enabled for this user' : '2FA is not enabled'}
                </p>
              </div>
              {user.totp_enabled ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => reset2FAMutation.mutate(user.id)}
                  disabled={reset2FAMutation.isLoading}
                  style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,.3)' }}
                >
                  {reset2FAMutation.isLoading ? 'Resetting…' : 'Reset 2FA'}
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {user.mfa_required ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => cancelForceMfaMutation.mutate(user.id)}
                      disabled={cancelForceMfaMutation.isLoading}
                      style={{ color: '#f59e0b' }}
                    >
                      {cancelForceMfaMutation.isLoading ? 'Removing…' : 'Cancel Requirement'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => forceMfaMutation.mutate(user.id)}
                      disabled={forceMfaMutation.isLoading}
                      style={{ color: 'var(--accent)' }}
                    >
                      {forceMfaMutation.isLoading ? 'Forcing…' : 'Force MFA Setup'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary">
            {user ? 'Update' : 'Create'} User
          </button>
        </div>
      </form>
    )
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
        <LoadingSpinner size="lg" text="Loading users..." />
      </div>
    )
  }

  // Calculate stats
  const totalUsers = displayUsers.length
  const activeUsers = displayUsers.filter(u => u.status === 'active').length
  const adminUsers = displayUsers.filter(u => u.role === 'admin').length
  const clientUsers = displayUsers.filter(u => u.role === 'client').length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingTop: 4 }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Users</div>
          <h1>User Management</h1>
          <div className="subtitle">Manage system users, roles, and permissions</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus style={{ width: 15, height: 15 }} />
          Add User
        </button>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        <StatCard title="Total Users"  value={totalUsers}  icon={Users}     index={0} />
        <StatCard title="Active Users" value={activeUsers} icon={UserCheck}  index={1} />
        <StatCard title="Admin Users"  value={adminUsers}  icon={Shield}    index={2} />
        <StatCard title="Client Users" value={clientUsers} icon={Activity}  index={3} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 360 }}>
          <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: 'var(--text-faint)' }} />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 32 }}
          />
        </div>
        <div style={{ position: 'relative' }}>
          <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} style={{ ...selectStyle, width: 'auto', paddingRight: 28 }}>
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="client">Client</option>
            <option value="moderator">Moderator</option>
          </select>
          <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--text-faint)', pointerEvents: 'none' }} />
        </div>
        <div style={{ position: 'relative' }}>
          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={{ ...selectStyle, width: 'auto', paddingRight: 28 }}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="banned">Banned</option>
          </select>
          <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--text-faint)', pointerEvents: 'none' }} />
        </div>
      </div>

      {/* Users Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-0)' }}>
                {['User', 'Role', 'Status', 'Activity', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => {
                const row = (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.name || 'Unnamed User'}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.email}
                          </div>
                          {user.company_name && (
                            <div style={{ fontSize: 11, color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {user.company_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <RoleBadge role={user.role} />
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {user.role === 'client' ? (() => {
                          const isFreePlan = plansRaw?.[user.subscription_plan]?.price === 0
                          const s = isFreePlan ? 'active' : user.billing_status
                          const cfg = {
                            active:   { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', Icon: CheckCircle,  label: 'ACTIVE' },
                            trialing: { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', Icon: CheckCircle,  label: 'TRIALING' },
                            past_due: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', Icon: XCircle,      label: 'PAST DUE' },
                            unpaid:   { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', Icon: XCircle,      label: 'UNPAID' },
                          }
                          const { bg, color, Icon, label } = cfg[s] || { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', Icon: Clock, label: 'PENDING' }
                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: bg, color, whiteSpace: 'nowrap' }}>
                              <Icon style={{ width: 11, height: 11 }} />
                              {label}
                            </span>
                          )
                        })() : <StatusBadge status={user.status} isVerified={user.is_verified} />}
                        {user.totp_enabled ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: 'rgba(91,192,235,0.15)', color: '#5BC0EB' }}>
                            <Shield style={{ width: 11, height: 11 }} />
                            2FA
                          </span>
                        ) : user.mfa_required ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
                            <ShieldCheck style={{ width: 11, height: 11 }} />
                            MFA Required
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, color: 'var(--text)' }}>{user.login_count || 0} logins</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                        {user.last_login ? `Last: ${new Date(user.last_login).toLocaleDateString()}` : 'Never logged in'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, color: 'var(--text)' }}>
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                        {(() => {
                          const days = Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))
                          if (days === 0) return 'Today'
                          if (days === 1) return 'Yesterday'
                          if (days < 30) return `${days} days ago`
                          if (days < 365) return `${Math.floor(days / 30)} months ago`
                          return `${Math.floor(days / 365)} years ago`
                        })()}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {/* Edit */}
                        <button
                          title="Edit"
                          className="btn-icon"
                          onClick={() => setEditingUser(user)}
                          style={{ color: 'var(--text-muted)' }}
                        >
                          <Edit style={{ width: 16, height: 16 }} />
                        </button>
                        {/* Reset password */}
                        <button
                          title="Reset Password"
                          className="btn-icon"
                          onClick={() => setResetPasswordUser(user)}
                          style={{ color: 'var(--text-muted)' }}
                        >
                          <KeyRound style={{ width: 16, height: 16 }} />
                        </button>
                        {/* Toggle status */}
                        <button
                          title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                          className="btn-icon"
                          onClick={() => handleToggleStatus(user)}
                          style={{ color: user.status === 'active' ? '#f59e0b' : '#22c55e' }}
                        >
                          {user.status === 'active' ? <XCircle style={{ width: 16, height: 16 }} /> : <CheckCircle style={{ width: 16, height: 16 }} />}
                        </button>
                        {/* Force MFA */}
                        {!user.totp_enabled && (
                          <button
                            title={user.mfa_required ? 'Cancel Force MFA' : 'Force MFA Setup'}
                            className="btn-icon"
                            onClick={() => user.mfa_required ? cancelForceMfaMutation.mutate(user.id) : forceMfaMutation.mutate(user.id)}
                            disabled={forceMfaMutation.isLoading || cancelForceMfaMutation.isLoading}
                            style={{ color: user.mfa_required ? '#f59e0b' : 'var(--text-muted)' }}
                          >
                            {user.mfa_required ? <ShieldOff style={{ width: 16, height: 16 }} /> : <ShieldCheck style={{ width: 16, height: 16 }} />}
                          </button>
                        )}
                        {/* Delete */}
                        <button
                          title="Delete"
                          className="btn-icon"
                          onClick={() => handleDeleteUser(user)}
                          disabled={deleteUserMutation.isLoading}
                          style={{ color: '#ef4444' }}
                        >
                          <Trash2 style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )

                if (hasInitiallyLoaded) return row

                return (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    style={{ borderBottom: '1px solid var(--border)' }}
                  >
                    {row.props.children}
                  </motion.tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: 12 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(108,99,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users style={{ width: 28, height: 28, color: 'var(--accent)' }} />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', margin: 0 }}>No users found</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', maxWidth: 380, margin: 0 }}>
            {searchTerm || selectedRole !== 'all' || selectedStatus !== 'all'
              ? 'No users match your search criteria. Try adjusting your filters.'
              : 'Get started by creating your first user account.'}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus style={{ width: 14, height: 14 }} /> Add User
            </button>
            {(searchTerm || selectedRole !== 'all' || selectedStatus !== 'all') && (
              <button className="btn btn-ghost" onClick={() => { setSearchTerm(''); setSelectedRole('all'); setSelectedStatus('all') }}>
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add User Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add New User">
        <UserForm onSubmit={(userData) => createUserMutation.mutate(userData)} onCancel={() => setShowAddModal(false)} />
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={!!resetPasswordUser} onClose={() => setResetPasswordUser(null)} title="Reset Password">
        {resetPasswordUser && (
          <ResetPasswordForm
            user={resetPasswordUser}
            onSubmit={({ password }) => resetPasswordMutation.mutate({ id: resetPasswordUser.id, password })}
            onCancel={() => setResetPasswordUser(null)}
            isLoading={resetPasswordMutation.isLoading}
          />
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={!!editingUser} onClose={() => setEditingUser(null)} title="Edit User">
        {editingUser && (
          <UserForm
            user={editingUser}
            onSubmit={(userData) => updateUserMutation.mutate({ id: editingUser.id, ...userData })}
            onCancel={() => setEditingUser(null)}
          />
        )}
      </Modal>

      {/* Delete User Confirmation Modal */}
      <Modal isOpen={!!userToDelete} onClose={() => setUserToDelete(null)} title="Delete User">
        {userToDelete && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Trash2 style={{ width: 22, height: 22, color: '#ef4444' }} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: '0 0 6px' }}>
                  Delete User Account
                </h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  Are you sure you want to permanently delete <span style={{ fontWeight: 600, color: 'var(--text)' }}>{userToDelete.name}</span> ({userToDelete.email})?
                </p>
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <AlertCircle style={{ width: 16, height: 16, color: '#ef4444', flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 12.5, color: '#ef4444' }}>
                      <strong>Warning:</strong> This action cannot be undone. All user data, including associated sites and settings, will be permanently deleted.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setUserToDelete(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleteUserMutation.isLoading}
              >
                {deleteUserMutation.isLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default UserManager
